import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import { usePostCoPilotConfig } from '@/api/rest/resources/organization';
import {
  CoPilotConfig,
  GET_CO_PILOT_CONFIG_PATH,
  GetCoPilotConfigErrorDto,
  GetCoPilotConfigSuccessDto,
} from '@/api/rest/resources/types/organization';
import { assertIsDefined } from '@/utils/assert';

import { useUserOrganizationId } from './useUserOrganizationId';

export const useCreateCoPilotConfig = () => {
  const postCoPilotConfigMutation = usePostCoPilotConfig();

  const orgId = useUserOrganizationId();

  const submit = async (copilotConfig: CoPilotConfig, organizationIdArg?: string) => {
    const organizationId = organizationIdArg ?? orgId;

    assertIsDefined(organizationId, 'organizationId');

    return postCoPilotConfigMutation.mutateAsync({
      pathVariables: { organization_id: organizationId },
      bodyData: copilotConfig,
    });
  };

  const { isPending, error } = postCoPilotConfigMutation;

  return { submit, isPending, error };
};

type SuccessDto = GetCoPilotConfigSuccessDto;
type ErrorDto = GetCoPilotConfigErrorDto;

export const useGetCoPilotConfig = (organizationIdArg?: string) => {
  const orgId = useUserOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(organizationId, 'organizationId');

  const pathVariables = { organization_id: organizationId };

  const queryFn = async () => {
    return getRequest<SuccessDto>(GET_CO_PILOT_CONFIG_PATH, pathVariables);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['organization', 'co-pilot-config', pathVariables],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};
